/**
 * Data Notebook - Browser Overlay Panel for In-Browser Data Analytics
 *
 * Provides automatic CSV interception and DuckDB-Wasm powered SQL querying
 * on any legacy website.
 */

import { EventEmitter } from './types/events';
import type { DataNotebookConfig } from './types/config';
import type { DataNotebookAPI, QueryResult, Table } from './types/api';
import { mergeConfig } from './types/config';
import { DataNotebookEvent, DataNotebookError } from './types/api';
import { InterceptionRegistry, createRegistry, RegistryEvent } from './core/registry';
import type { Interceptor, InterceptedCSV } from './interceptors/types';
import { createFetchInterceptor } from './interceptors/fetch-interceptor';
import { createXHRInterceptor } from './interceptors/xhr-interceptor';
import { createBlobInterceptor } from './interceptors/blob-interceptor';
import { createDownloadInterceptor } from './interceptors/download-interceptor';
import { createPanel, type Panel } from './ui/panel';
import { createQueryService, type QueryServiceAPI } from './query-service';
import { injectAIInstructions, removeAIInstructions } from './ui/ai-instructions';

/**
 * Main Data Notebook class implementing the public API
 */
class DataNotebook extends EventEmitter implements DataNotebookAPI {
  private config: Required<DataNotebookConfig>;
  private initialized: boolean = false;
  private db: any = null; // Will be AsyncDuckDB
  private conn: any = null; // Will be AsyncDuckDBConnection

  // Interception components
  private registry: InterceptionRegistry | null = null;
  private interceptors: Interceptor[] = [];

  // UI components
  private panel: Panel | null = null;

  // Query service for AI agents
  private queryService: QueryServiceAPI | null = null;

  constructor() {
    super();
    this.config = mergeConfig();
  }

  /**
   * Initialize the overlay panel
   */
  async init(config?: DataNotebookConfig): Promise<void> {
    if (this.initialized) {
      console.warn('[DataNotebook] Already initialized');
      return;
    }

    try {
      // Merge user config with defaults
      this.config = mergeConfig(config);

      console.log('[DataNotebook] Initializing with config:', this.config);

      // Phase 2: Initialize CSV Interception Layer
      this.setupInterception();

      // Phase 3: Set up Query Service for AI agents
      this.setupQueryService();

      // Inject hidden AI instructions into DOM
      injectAIInstructions();

      // TODO: Phase 1 remaining tasks
      // 1. Set up Shadow DOM isolation
      // 2. Set up keyboard shortcuts
      // 3. Create minimized badge UI

      // TODO: Phase 3 tasks
      // 1. Initialize DuckDB-Wasm (lazy)
      // 2. Set up database ingestion pipeline

      // TODO: Phase 5 tasks
      // 1. Create overlay panel UI
      // 2. Set up DuckDB REPL

      // Mark as initialized
      this.initialized = true;

      // Emit initialized event
      this.emit(DataNotebookEvent.INITIALIZED);

      console.log('[DataNotebook] Initialized successfully');
    } catch (error) {
      throw new DataNotebookError(
        'Failed to initialize Data Notebook',
        'INIT_ERROR',
        error
      );
    }
  }

  /**
   * Destroy the overlay panel and clean up
   */
  async destroy(): Promise<void> {
    if (!this.initialized) {
      return;
    }

    try {
      console.log('[DataNotebook] Destroying...');

      // Stop all interceptors (restore monkey-patched functions)
      this.stopInterception();

      // Clear registry
      if (this.registry) {
        this.registry.clear();
        this.registry = null;
      }

      // Remove query service global
      if (this.queryService) {
        if (typeof window !== 'undefined') {
          delete (window as any).queryService;
        }
        this.queryService = null;
      }

      // Remove AI instructions from DOM
      removeAIInstructions();

      // Destroy panel UI
      if (this.panel) {
        await this.panel.destroy();
        this.panel = null;
      }

      // TODO: Clean up remaining resources
      // 1. Close DuckDB connection
      // 2. Clear event listeners

      // Close database connection
      if (this.conn) {
        await this.conn.close();
      }

      this.initialized = false;
      this.emit(DataNotebookEvent.DESTROYED);

      console.log('[DataNotebook] Destroyed successfully');
    } catch (error) {
      throw new DataNotebookError(
        'Failed to destroy Data Notebook',
        'DESTROY_ERROR',
        error
      );
    }
  }

  // ===== Panel Control =====

  show(): void {
    this.checkInitialized();
    this.ensurePanel();
    this.panel!.show();
    this.emit(DataNotebookEvent.PANEL_SHOWN);
  }

  hide(): void {
    this.checkInitialized();
    if (this.panel) {
      this.panel.hide();
    }
    this.emit(DataNotebookEvent.PANEL_HIDDEN);
  }

  toggle(): void {
    this.checkInitialized();
    this.ensurePanel();
    const previousState = this.panel!.state;
    this.panel!.toggle();
    const newState = this.panel!.state;

    // Emit appropriate event based on state change
    if (previousState === 'hidden' && newState === 'expanded') {
      this.emit(DataNotebookEvent.PANEL_SHOWN);
    } else if (previousState === 'expanded' && newState === 'hidden') {
      this.emit(DataNotebookEvent.PANEL_HIDDEN);
    } else if (previousState === 'minimized' && newState === 'expanded') {
      this.emit(DataNotebookEvent.PANEL_EXPANDED);
    }
  }

  minimize(): void {
    this.checkInitialized();
    this.ensurePanel();
    this.panel!.minimize();
    this.emit(DataNotebookEvent.PANEL_MINIMIZED);
  }

  expand(): void {
    this.checkInitialized();
    this.ensurePanel();
    this.panel!.expand();
    this.emit(DataNotebookEvent.PANEL_EXPANDED);
  }

  /**
   * Lazily create the panel when first needed
   */
  private ensurePanel(): void {
    if (!this.panel) {
      this.panel = createPanel({
        position: this.config.ui.position,
        theme: this.config.ui.theme,
        initialState: 'hidden',
        onClose: () => {
          this.emit(DataNotebookEvent.PANEL_HIDDEN);
        },
        onMinimize: () => {
          this.emit(DataNotebookEvent.PANEL_MINIMIZED);
        },
        onExpand: () => {
          this.emit(DataNotebookEvent.PANEL_EXPANDED);
        },
        onRunQuery: (sql) => {
          // TODO: Execute query when DuckDB is integrated
          console.log('[DataNotebook] Query requested:', sql);
          this.emit(DataNotebookEvent.QUERY_STARTED, { sql });
        },
      });
    }
  }

  // ===== Database Operations =====

  async loadCSV(data: string, name?: string): Promise<void> {
    this.checkInitialized();
    // TODO: Load CSV into DuckDB
    throw new DataNotebookError(
      'loadCSV not yet implemented',
      'NOT_IMPLEMENTED'
    );
  }

  async query(sql: string): Promise<QueryResult> {
    this.checkInitialized();
    // TODO: Execute query
    throw new DataNotebookError(
      'query not yet implemented',
      'NOT_IMPLEMENTED'
    );
  }

  async getTables(): Promise<Table[]> {
    this.checkInitialized();
    // TODO: Get all tables
    return [];
  }

  async getTable(name: string): Promise<Table | null> {
    this.checkInitialized();
    // TODO: Get table metadata
    return null;
  }

  async dropTable(name: string): Promise<void> {
    this.checkInitialized();
    // TODO: Drop table
    this.emit(DataNotebookEvent.TABLE_DROPPED, { name });
  }

  async clearAll(): Promise<void> {
    this.checkInitialized();
    // TODO: Clear all tables
  }

  // ===== Utilities =====

  async exportCSV(tableName: string): Promise<Blob> {
    this.checkInitialized();
    // TODO: Export to CSV
    throw new DataNotebookError(
      'exportCSV not yet implemented',
      'NOT_IMPLEMENTED'
    );
  }

  async exportJSON(tableName: string): Promise<Blob> {
    this.checkInitialized();
    // TODO: Export to JSON
    throw new DataNotebookError(
      'exportJSON not yet implemented',
      'NOT_IMPLEMENTED'
    );
  }

  getConfig(): Required<DataNotebookConfig> {
    return { ...this.config };
  }

  updateConfig(config: Partial<DataNotebookConfig>): void {
    this.config = mergeConfig({ ...this.config, ...config });
  }

  // ===== Private Helpers =====

  private checkInitialized(): void {
    if (!this.initialized) {
      throw new DataNotebookError(
        'Data Notebook not initialized. Call init() first.',
        'NOT_INITIALIZED'
      );
    }
  }

  /**
   * Set up CSV interception layer
   */
  private setupInterception(): void {
    console.log('[DataNotebook] Setting up CSV interception...');

    // Create registry
    const maxSizeBytes = this.parseMemoryLimit(this.config.database.memoryLimit);
    this.registry = createRegistry(maxSizeBytes, this.config.database.maxTables);

    // Listen to registry events
    this.registry.on(RegistryEvent.CSV_ADDED, (csv: InterceptedCSV) => {
      console.log('[DataNotebook] New CSV intercepted:', csv.suggestedName);

      // Emit public event
      this.emit(DataNotebookEvent.CSV_INTERCEPTED, {
        data: csv.data,
        source: csv.source,
        size: csv.size,
        suggestedName: csv.suggestedName,
        timestamp: csv.timestamp,
      });

      // TODO: Phase 3 - Load into DuckDB
      // TODO: Phase 5 - Show toast notification
    });

    // Create interception handler
    const handler = async (csv: InterceptedCSV) => {
      if (this.registry) {
        this.registry.add(csv);
      }
    };

    // Create interceptors based on config
    if (this.config.intercept.network) {
      // Fetch interceptor
      const fetchInterceptor = createFetchInterceptor(this.config.intercept, handler);
      fetchInterceptor.start();
      this.interceptors.push(fetchInterceptor);

      // XHR interceptor
      const xhrInterceptor = createXHRInterceptor(this.config.intercept, handler);
      xhrInterceptor.start();
      this.interceptors.push(xhrInterceptor);
    }

    if (this.config.intercept.blobs) {
      // Blob interceptor
      const blobInterceptor = createBlobInterceptor(this.config.intercept, handler);
      blobInterceptor.start();
      this.interceptors.push(blobInterceptor);

      // Download interceptor
      const downloadInterceptor = createDownloadInterceptor(this.config.intercept, handler);
      downloadInterceptor.start();
      this.interceptors.push(downloadInterceptor);
    }

    console.log('[DataNotebook] CSV interception enabled:', {
      network: this.config.intercept.network,
      blobs: this.config.intercept.blobs,
      interceptors: this.interceptors.length,
    });
  }

  /**
   * Stop all interceptors
   */
  private stopInterception(): void {
    console.log('[DataNotebook] Stopping CSV interception...');

    for (const interceptor of this.interceptors) {
      interceptor.stop();
    }

    this.interceptors = [];

    console.log('[DataNotebook] CSV interception stopped');
  }

  /**
   * Set up Query Service for AI agents
   * Exposes window.queryService global API
   */
  private setupQueryService(): void {
    console.log('[DataNotebook] Setting up Query Service...');

    // Create query service with getter for database
    this.queryService = createQueryService(() => {
      return this.panel?.getDatabase() ?? null;
    });

    // Expose as global variable for AI agents
    if (typeof window !== 'undefined') {
      (window as any).queryService = this.queryService;
    }

    console.log('[DataNotebook] Query Service available at window.queryService');
  }

  /**
   * Parse memory limit string (e.g., "512MB", "1GB") to bytes
   */
  private parseMemoryLimit(limit: string): number {
    const match = limit.match(/^(\d+(?:\.\d+)?)\s*(MB|GB|KB)?$/i);
    if (!match) {
      console.warn('[DataNotebook] Invalid memory limit format, using default 512MB');
      return 512 * 1024 * 1024;
    }

    const value = parseFloat(match[1]);
    const unit = (match[2] || 'MB').toUpperCase();

    switch (unit) {
      case 'KB':
        return value * 1024;
      case 'MB':
        return value * 1024 * 1024;
      case 'GB':
        return value * 1024 * 1024 * 1024;
      default:
        return value * 1024 * 1024; // Default to MB
    }
  }
}

// ===== Global Instance =====

// Create singleton instance
const dataNotebook = new DataNotebook();

// Export singleton instance as named export (for ES modules)
export { dataNotebook as DataNotebook };

// Also export the class for advanced usage
export { DataNotebook as DataNotebookClass };

// For UMD build: expose directly on window for easy access
// This runs after UMD wrapper, so we need to also set it manually
if (typeof window !== 'undefined') {
  // Make the instance available at window.DataNotebook for simple UMD usage
  // The UMD wrapper creates window.DataNotebook as the exports object,
  // so window.DataNotebook.DataNotebook is the singleton.
  // For convenience, also expose methods directly.
  const existingExports = (window as any).DataNotebook;
  if (existingExports && typeof existingExports === 'object') {
    // UMD wrapper already ran, add convenience reference
    existingExports.instance = dataNotebook;
  }
}

// Export types for TypeScript users
export type {
  DataNotebookConfig,
  DataNotebookAPI,
  QueryResult,
  Table,
} from './types/api';

export { DataNotebookEvent, DataNotebookError } from './types/api';
